import React, { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function iconFor(kind) {
  if (kind === "topup_approved") return <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2.2} />;
  if (kind === "topup_rejected") return <XCircle className="w-4 h-4 text-rose-400" strokeWidth={2.2} />;
  return <Sparkles className="w-4 h-4 text-fuchsia-400" strokeWidth={2.2} />;
}

export function NotificationBell({ compact = false }) {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // silent — bell shouldn't crash the shell
    }
  }, [user]);

  // Poll every 20s while logged in
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const id = setInterval(fetchNotifications, 20000);
    return () => clearInterval(id);
  }, [user, fetchNotifications]);

  // Refresh when popover opens (to catch newly delivered items)
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markAllRead = async () => {
    if (unread === 0) return;
    setLoading(true);
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch (e) { /* ignore */ }
    }
    // On approval, credits balance changed — refresh the user object
    if (n.kind === "topup_approved" && refreshUser) {
      refreshUser();
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label="Notifications"
          className={`relative flex items-center justify-center rounded-full transition-colors ${
            compact ? "w-8 h-8" : "w-9 h-9"
          } text-zinc-400 hover:text-white hover:bg-white/5`}
        >
          <Bell className={compact ? "w-4 h-4" : "w-[18px] h-[18px]"} strokeWidth={2} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-fuchsia-500 text-[10px] font-bold text-white flex items-center justify-center border border-[#09090B]"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[360px] p-0 bg-[#121214] border border-white/10 text-zinc-100 shadow-2xl"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm font-semibold tracking-tight">Notifications</div>
          <button
            data-testid="notification-mark-all-read"
            onClick={markAllRead}
            disabled={unread === 0 || loading}
            className="text-[11px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300 disabled:text-zinc-600 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="notification-empty">
              <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" strokeWidth={1.6} />
              You&apos;re all caught up.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => handleItemClick(n)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3 ${
                  !n.read ? "bg-white/[0.03]" : ""
                }`}
              >
                <div className="mt-0.5 shrink-0">{iconFor(n.kind)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`text-sm ${!n.read ? "font-semibold text-white" : "text-zinc-300"}`}>
                      {n.title}
                    </div>
                    {!n.read && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-fuchsia-400 shrink-0" />
                    )}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{n.body}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">
                    {timeAgo(n.created_at)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
